import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import './Profile.css';
import { getStoredProfile } from '../utils/profileStorage';
import { getStoredUser } from '../utils/authStorage';

const formatRole = (role) => {
  if (!role) return '';
  const roleLower = role.toLowerCase();
  if (roleLower === 'user') return 'Viewer';
  if (roleLower === 'technician') return 'Developer';
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
};

const buildProfile = () => {
  const storedProfile = getStoredProfile();
  const authUser = getStoredUser();

  const derivedName =
    storedProfile.name ||
    authUser?.fullName ||
    authUser?.username ||
    (authUser?.email ? authUser.email.split('@')[0] : 'WebMonitor User');

  return {
    ...storedProfile,
    name: derivedName,
    title: storedProfile.title || 'Web Operations Lead',
    company: storedProfile.company || 'Northstar Digital',
    email: storedProfile.email || authUser?.email || '',
    role: authUser?.role || '',
    phone: storedProfile.phone || '+1 (415) 222-0198',
    timezone: storedProfile.timezone || 'GMT-08:00 Pacific Time',
    plan: storedProfile.plan || 'Agency Pro',
    seats: storedProfile.seats || 12,
    created: storedProfile.created || 'January 12, 2024',
    avatar: storedProfile.avatar || '',
  };
};

const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(buildProfile);

  const [notifications, setNotifications] = useState({
    incidents: true,
    weeklyReport: true,
    productUpdates: false
  });

  useEffect(() => {
    const syncProfile = () => setProfile(buildProfile());

    if (typeof window !== 'undefined') {
      window.addEventListener('profile-updated', syncProfile);
      window.addEventListener('storage', syncProfile);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('profile-updated', syncProfile);
        window.removeEventListener('storage', syncProfile);
      }
    };
  }, []);

  const avatarLabel = useMemo(() => {
    const source = profile?.name || profile?.email || 'WM';
    return source
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || 'WM';
  }, [profile?.name, profile?.email]);

  const toggleNotification = (key) =>
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="dashboard-container profile-page">
      <Sidebar />
      <div className="main-content">
        <header className="profile-hero">
          <div>
            <p className="eyebrow">Account / Profile</p>
            <h1>Profile & Preferences</h1>
            <p className="subheading">
              Manage your operator identity, contact details, and notification preferences for every property you monitor.
            </p>
          </div>
          <button type="button" className="ghost-link pill">Request account handoff</button>
        </header>

        <section className="profile-grid">
          <div className="profile-card personal-card">
            <div className="card-header">
              <div>
                <h2>Personal details</h2>
                <p>Visible to teammates when collaborating on incidents.</p>
              </div>
              <button type="button" className="btn-primary" onClick={() => navigate('/profile/edit')}>
                Edit Profile
              </button>
            </div>
            <div className="personal-info">
              <div className="avatar-xl">{avatarLabel}</div>
              <div>
                <h3>{profile.name}</h3>
                <p>{profile.title} · {profile.company}</p>
                <div className="info-list">
                  <span>Email</span>
                  <p>{profile.email}</p>
                  {profile.role && (
                    <>
                      <span>Role</span>
                      <p className="info-role">{formatRole(profile.role)}</p>
                    </>
                  )}
                  <span>Phone</span>
                  <p>{profile.phone}</p>
                  <span>Timezone</span>
                  <p>{profile.timezone}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="profile-card plan-card">
            <h2>Subscription</h2>
            <div className="plan-details">
              <div>
                <p>Current plan</p>
                <h3>{profile.plan}</h3>
                <span>Created {profile.created}</span>
              </div>
              <div>
                <p>Seats in use</p>
                <h3>{profile.seats}</h3>
                <span>Team licenses</span>
              </div>
            </div>
            <button type="button" className="btn-secondary">Upgrade plan</button>
          </div>
        </section>

        <section className="profile-grid">
          <div className="profile-card">
            <div className="card-header">
              <div>
                <h2>Notification preferences</h2>
                <p>Choose when WebMonitor should reach out.</p>
              </div>
            </div>
            <div className="notification-list">
              <label>
                <input
                  type="checkbox"
                  checked={notifications.incidents}
                  onChange={() => toggleNotification('incidents')}
                />
                Critical incident alerts
                <span>Triggers SMS + email when downtime exceeds SLA.</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={notifications.weeklyReport}
                  onChange={() => toggleNotification('weeklyReport')}
                />
                Weekly executive digest
                <span>Performance summary delivered every Monday.</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={notifications.productUpdates}
                  onChange={() => toggleNotification('productUpdates')}
                />
                Product updates
                <span>New features, beta invites, and roadmap notes.</span>
              </label>
            </div>
          </div>

          <div className="profile-card activity-card">
            <div className="card-header">
              <div>
                <h2>Recent activity</h2>
                <p>Latest actions completed with this account.</p>
              </div>
            </div>
            <ul>
              <li>
                <strong>Updated Google uptime policy</strong>
                <span>2 hours ago · via desktop</span>
              </li>
              <li>
                <strong>Shared report link with stakeholders</strong>
                <span>Yesterday · automatic schedule</span>
              </li>
              <li>
                <strong>Invited Priya Desai to workspace</strong>
                <span>Nov 14 · manual invite</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Profile;

